const quantityFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 });
const euroFormat = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

// Only public sale prices belong in this model. Supplier costs stay off the storefront.
export function validatePackagingSpec(raw) {
  if (!raw || raw.schemaVersion !== 1 || !['preview', 'content'].includes(raw.mode)) return null;
  const amount = Number(raw.contentQuantity);
  if (!Number.isFinite(amount) || amount <= 0 || !Number.isSafeInteger(amount * 1000)) return null;
  if (![raw.unitSingular, raw.unitPlural].every(value => typeof value === 'string' && value.trim().length > 0)) return null;
  const minimum = raw.minimumPackQuantity ?? 1;
  if (!Number.isSafeInteger(minimum) || minimum < 1) return null;
  const spec = { mode: raw.mode, amount, minimum, unitSingular: raw.unitSingular, unitPlural: raw.unitPlural };
  if (raw.mode === 'preview') {
    if (raw.currency !== 'EUR' || raw.taxBasis !== 'net' || raw.checkoutReady !== false) return null;
    if (!Array.isArray(raw.saleTiers) || raw.saleTiers.length === 0) return null;
    const tiers = raw.saleTiers.map(tier => ({ from: tier.fromPackQuantity, cents: tier.netPriceCents }));
    if (tiers[0].from !== minimum || tiers.some((tier, index) =>
      !Number.isSafeInteger(tier.from) || tier.from < 1 ||
      !Number.isSafeInteger(tier.cents) || tier.cents < 1 ||
      (index > 0 && (tier.from <= tiers[index - 1].from || tier.cents > tiers[index - 1].cents)))) return null;
    spec.tiers = tiers;
  }
  return spec;
}

export function calculatePackaging(spec, value, constraints = {}) {
  if (!spec) return null;
  const text = String(value).trim();
  // An empty field, decimals and exponent notation must not silently become an order.
  if (!/^\d+$/.test(text)) return null;
  const packs = Number(text);
  const min = Number(constraints.min ?? 1);
  const step = Number(constraints.step ?? 1);
  const max = constraints.max == null || constraints.max === '' ? Infinity : Number(constraints.max);
  if (!Number.isSafeInteger(min) || min < 0 || !(max === Infinity || Number.isSafeInteger(max)) ||
      !Number.isSafeInteger(packs) || packs < Math.max(spec.minimum, 1, min) || packs > max ||
      !Number.isSafeInteger(step) || step < 1 || (packs - min) % step !== 0) return null;
  const thousandths = packs * Math.round(spec.amount * 1000);
  if (!Number.isSafeInteger(thousandths)) return null;
  const amount = thousandths / 1000;
  const unit = amount === 1 ? spec.unitSingular : spec.unitPlural;
  const result = { packs, amount, summary: `${quantityFormat.format(packs)} ${packs === 1 ? 'Verpackungseinheit' : 'Verpackungseinheiten'} = ${quantityFormat.format(amount)} ${unit}` };
  if (spec.tiers) {
    const tier = spec.tiers.findLast(item => packs >= item.from);
    if (!tier) return null;
    const totalCents = packs * tier.cents;
    if (!Number.isSafeInteger(totalCents)) return null;
    Object.assign(result, { priceCents: tier.cents, totalCents, tierFrom: tier.from,
      unitPrice: euroFormat.format(tier.cents / 100), total: euroFormat.format(totalCents / 100) });
  }
  return result;
}

export class RVPackagingCalculator extends HTMLElement {
  #scope;
  #spec;
  #observer;
  connectedCallback() {
    try { this.#spec = validatePackagingSpec(JSON.parse(this.dataset.spec)); } catch { this.#spec = null; }
    this.#scope = this.dataset.ownInput === 'true' ? this : this.closest('product-form-component');
    if (!this.#spec || !this.#scope) {
      this.hidden = true;
      return;
    }
    this.hidden = false;
    for (const event of ['input', 'change', 'quantity-selector:update']) this.#scope.addEventListener(event, this.#update);
    this.#observer = new MutationObserver(this.#update);
    this.#observer.observe(this.#scope, { childList: true, subtree: true, attributes: true, attributeFilter: ['min', 'max', 'step', 'value'] });
    this.#update();
  }

  disconnectedCallback() {
    for (const event of ['input', 'change', 'quantity-selector:update']) this.#scope?.removeEventListener(event, this.#update);
    this.#observer?.disconnect();
  }

  #set(selector, value) {
    const node = this.querySelector(selector);
    if (node && node.textContent !== value) node.textContent = value;
  }

  #update = () => {
    const input = this.#scope?.querySelector('input[name="quantity"]');
    if (!input) { this.hidden = true; return; }
    this.hidden = false;
    const quote = calculatePackaging(this.#spec, input.value, {
      min: input.getAttribute('min') || 1, max: input.getAttribute('max'), step: input.getAttribute('step') || 1,
    });
    const invalid = this.#spec.minimum > 1
      ? `Bitte mindestens ${quantityFormat.format(this.#spec.minimum)} ganze Verpackungseinheiten eingeben und die angezeigten Mengenschritte beachten.`
      : 'Bitte eine gültige Anzahl ganzer Verpackungseinheiten eingeben.';
    this.#set('[data-rv-total-content]', quote?.summary ?? invalid);
    this.#set('[data-rv-unit-price]', quote?.unitPrice ?? '—');
    this.#set('[data-rv-total-price]', quote?.total ?? '—');
    for (const row of this.querySelectorAll('[data-rv-tier]')) {
      const selected = Number(row.dataset.rvTier) === quote?.tierFrom;
      row.toggleAttribute('data-selected', selected);
      if (selected) row.setAttribute('aria-current', 'true'); else row.removeAttribute('aria-current');
    }
  };
}

if (!customElements.get('rv-packaging-calculator')) customElements.define('rv-packaging-calculator', RVPackagingCalculator);
